import React, {useCallback, useState} from 'react';

import {browserHistory} from 'react-router';

import {AddDocumentsToTeamPopover} from 'app/components/general/GenericDocumentActionButtons/AddDocumentsToTeamPopover';
import {CloneDocumentModal} from 'app/components/general/GenericDocumentActionButtons/CloneDocumentModal';
import {SignoffRequestModal} from 'app/components/general/GenericDocumentActionButtons/SignoffRequestModal';
import {AddToFolderPopover} from 'app/folders/components/AddToFolderPopover';
import {RemoveFromFolderModal} from 'app/folders/components/RemoveFromFolderModal';
import {DeleteDocumentModal} from 'app/general/modals/DeleteDocumentModal';
import {ShareDocumentModal} from 'app/general/modals/ShareDocumentModal/ShareDocumentModal';
import {
  ActionButtonBuilderType,
  ActionMenuItemType,
  ActionsButton,
} from 'app/general/widgets/ActionsButton';
import {AddToKnowledgeBasePopover} from 'app/kbs/components/AddToKnowledgeBasePopover';
import {useCreateTemplate} from 'app/market/api';
import {PrintDocumentPopover} from 'app/procedures/components/PrintDocumentPopover';
import {EditTagsDocumentPopover} from 'app/tags/components/EditTagsDocumentPopover';
import {AssignNewTaskModal} from 'app/tasks/components/AssignTaskModal';
import {gt} from 'app/utils/i18n';

import type {ActionableDocumentType} from 'app/general/types';

export type ActionsContextType =
  | 'edit'
  | 'importing'
  | 'view-published'
  | 'dashboard'
  | 'folders';

interface ActionDefinitionType {
  icon: string;
  text: string;
  callback: () => unknown;
  condition?: () => boolean;
  hideContexts?: Array<ActionsContextType>;
}

interface Props {
  children?: React.ReactElement | ActionButtonBuilderType;
  pageContext: ActionsContextType;
  document: ActionableDocumentType;
  refreshData?: () => unknown;
  currentFolder?: void | string;
}

/* a button which opens a dropdown menu with avilable actions for the current document */
export function DocumentActionsButton({
  children,
  pageContext,
  document,
  refreshData,
  currentFolder,
}: Props): JSX.Element {
  const [createTemplate] = useCreateTemplate();
  const [isShowing, setShow] = useState<
    | null
    | 'edit-tags'
    | 'duplicate'
    | 'export'
    | 'folders'
    | 'remove-from-folder'
    | 'signoff-request'
    | 'knowledge-base'
    | 'delete'
    | 'share'
    | 'teams'
    | 'assign-as-task'
  >(null);

  const close = () => setShow(null);

  const [currButtonRef, setCurrButtonRef] = useState<null | HTMLElement>(null);

  const setButtonRef = useCallback((newButtonRef) => {
    if (newButtonRef != currButtonRef) {
      setCurrButtonRef(newButtonRef);
    }
  }, []);

  function goToEditPage(htmlURL: string) {
    browserHistory.push(`${htmlURL}edit/`);
  }

  const doc =
    document.content_type === 'document'
      ? {
          id: document.object_id,
          ct: document.document_content_type,
          hashid: document.document.hashid,
          url: document.document.url,
          versionct: document.document.current_version.content_type,
          versionid: document.document.current_version.id,
          doc_url: document.url,
          tags: document.document.tags,
          team_memberships: document.document.team_memberships,
          template_at: document.document.template_at,
        }
      : {
          id: document.id,
          ct: document.content_type,
          hashid: document.hashid,
          url: document.url,
          versionct: document.current_version.content_type,
          versionid: document.current_version.id,
          doc_url: document.url,
          tags: document.tags,
          team_memberships: document.team_memberships,
          template_at: document.template_at,
        };

  const isNotQuiz = doc.ct !== 'quiz';
  const isQuiz = !isNotQuiz;

  const availableActions = React.useMemo(() => {
    const {can_change, can_edit, can_delete, can_export, can_assign} = document;

    const actionsDefinitions: Array<ActionDefinitionType> = [
      {
        icon: 'fa fa-edit',
        text: gt('Edit'),
        callback: () => goToEditPage(document.html_url),
        hideContexts: ['edit', 'view-published', 'importing'],
        condition: () => can_change,
      },
      {
        icon: 'fa fa-tag',
        text: gt('Edit tags'),
        callback: () => setShow('edit-tags'),
        condition: () => can_edit && isNotQuiz,
        hideContexts: ['edit', 'importing'],
      },
      {
        icon: 'far fa-folder',
        text: gt('Folders'),
        callback: () => setShow('folders'),
        condition: () => can_edit,
        hideContexts: ['importing'],
      },
      {
        icon: 'fa fa-users',
        text: gt('Teams'),
        callback: () => setShow('teams'),
        condition: () => can_edit && isNotQuiz,
      },
      {
        icon: 'far fa-copy',
        text: gt('Duplicate'),
        callback: () => setShow('duplicate'),
        hideContexts: ['edit', 'folders', 'importing'],
        condition: () => can_change,
      },
      {
        icon: 'fa fa-share',
        text: gt('Share'),
        callback: () => setShow('share'),
        hideContexts: ['edit', 'importing'],
        condition: () => can_edit && isNotQuiz,
      },
      {
        icon: 'far fa-check-square',
        text: gt('Assign as Task'),
        callback: () => setShow('assign-as-task'),
        condition: () => can_assign && doc.ct !== 'policy' && isNotQuiz,
        hideContexts: ['edit', 'importing'],
      },
      {
        icon: 'far fa-file-pdf',
        text: gt('Export'),
        callback: () => setShow('export'),
        condition: () => can_export && isNotQuiz,
        hideContexts: ['edit', 'importing'],
      },
      {
        icon: 'far fa-book-open',
        text: gt('Knowledge Base'),
        callback: () => setShow('knowledge-base'),
        condition: () => can_edit && doc.ct !== 'proceduregroup' && isNotQuiz,
        hideContexts: ['importing'],
      },
      {
        icon: 'fa fa-signature',
        text: gt('Request Sign Off'),
        callback: () => setShow('signoff-request'),
        condition: () => {
          if (isQuiz) {
            // We don't allow signoffs for a quiz.
            return false;
          } else {
            return can_edit && document.content_type !== 'document'
              ? document.approved_at != null
              : document.content_type === 'document'
              ? document.document.approved_at != null
              : false;
          }
        },
        hideContexts: ['edit', 'importing'],
      },
      {
        icon: 'far fa-trash',
        text: gt('Move to Recycle Bin'),
        callback: () => setShow('delete'),
        condition: () => can_delete,
        hideContexts: ['importing'],
      },
      {
        icon: 'far fa-folder-minus',
        text: gt('Remove from folder'),
        callback: () => setShow('remove-from-folder'),
        hideContexts: ['importing'],
        condition: () =>
          document.content_type === 'document'
            ? document.folder != null && can_edit
            : false,
      },
      {
        icon: 'far fa-file-export',
        text: gt('Mark as template'),
        callback: () =>
          createTemplate({document: doc.url}).unwrap().then(promisedCallback),
        hideContexts: ['importing'],
        condition: () => can_edit && isNotQuiz && doc.template_at == null,
      },
    ];

    return actionsDefinitions
      .map((item) => ({
        key: item.icon,
        condition: (): boolean => true,
        hideContexts: [] as Array<ActionsContextType>,
        ...item,
      }))
      .filter(
        (item) =>
          item.condition() && item.hideContexts.indexOf(pageContext) === -1
      )
      .map(
        ({icon, text, callback, key}): ActionMenuItemType => ({
          icon,
          text,
          callback,
          key,
        })
      );
  }, [document, pageContext]);

  // these props are used on all popovers
  const popoversOverlayProps = {
    target: currButtonRef as HTMLElement,
    trigger: 'legacy',
    placement: 'bottom' as const,
    isOpen: true,
    toggle: close,
  };

  const promisedCallback = () =>
    Promise.resolve(refreshData != null ? refreshData() : null);

  return (
    <>
      {availableActions.length > 0 ? (
        <ActionsButton ref={setButtonRef} actions={availableActions}>
          {children}
        </ActionsButton>
      ) : null}
      {isShowing == null || currButtonRef == null ? null : isShowing ===
        'assign-as-task' ? (
        doc.ct !== 'policy' &&
        doc.ct !== 'quiz' &&
        typeof doc.id !== 'string' ? (
          <AssignNewTaskModal
            show={true}
            taskData={{
              content_type: doc.ct,
              object_id: doc.id,
            }}
            onToggle={close}
            onAssignSuccess={promisedCallback}
          />
        ) : null
      ) : isShowing === 'share' &&
        document.content_type !== 'quiz' &&
        doc.ct !== 'quiz' ? (
        <ShareDocumentModal
          show={true}
          contentType={doc.ct}
          object={document}
          onClose={close}
          hidePublicTab={true}
          initiallySelectedTab="show-team"
        />
      ) : isShowing === 'delete' ? (
        <DeleteDocumentModal
          show={true}
          object={{
            url: doc.url,
            name: document.name,
            content_type: doc.ct,
          }}
          redirectContentType={doc.ct}
          onClose={close}
          onDelete={pageContext === 'dashboard' ? promisedCallback : undefined}
        />
      ) : isShowing === 'duplicate' ? (
        <CloneDocumentModal
          contentType={doc.ct}
          documentTitle={document.name}
          documentURL={doc.url}
          isOpen={true}
          showFolder={isNotQuiz}
          onClone={promisedCallback}
          onClose={close}
        />
      ) : isShowing === 'export' && doc.versionct !== 'quizversion' ? (
        <PrintDocumentPopover
          overlayProps={popoversOverlayProps}
          objectId={doc.versionid}
          contentType={doc.versionct}
        />
      ) : isShowing === 'folders' ? (
        <AddToFolderPopover
          overlayProps={{...popoversOverlayProps, trigger: 'click'}}
          onSuccess={promisedCallback}
          documents={[
            {
              object_id: doc.id,
              document_content_type: doc.ct,
              url: doc.url,
            },
          ]}
          currentFolder={currentFolder}
        />
      ) : isShowing === 'knowledge-base' && doc.ct !== 'quiz' ? (
        <AddToKnowledgeBasePopover
          overlayProps={{...popoversOverlayProps, trigger: 'click'}}
          onSuccess={promisedCallback}
          documents={[
            {
              object_id: doc.id,
              document_content_type: doc.ct,
              url: doc.url,
            },
          ]}
        />
      ) : isShowing === 'remove-from-folder' ? (
        <RemoveFromFolderModal
          document={document}
          onClose={close}
          onSuccess={promisedCallback}
        />
      ) : isShowing === 'signoff-request' &&
        document.content_type !== 'quiz' &&
        doc.ct !== 'quiz' ? (
        <SignoffRequestModal
          document={document}
          isOpen={true}
          contentType={doc.ct}
          onClose={close}
          onSuccess={promisedCallback}
        />
      ) : isShowing == 'teams' ? (
        <AddDocumentsToTeamPopover
          documentURLs={[doc.doc_url]}
          documentObjectURLs={[doc.url]}
          teamMemberships={doc.team_memberships}
          onSuccess={promisedCallback}
          overlayProps={popoversOverlayProps}
        />
      ) : isShowing === 'edit-tags' ? (
        <EditTagsDocumentPopover
          overlayProps={popoversOverlayProps}
          documents={[
            {
              object_id: doc.id,
              document_content_type: doc.ct,
              tags: doc.tags,
            },
          ]}
          onSuccess={promisedCallback}
        />
      ) : null}
    </>
  );
}
